import { useMemo, useState } from "react";
import { convertCmToFeetInches } from "@/Utils";
import type { ScanDocData } from "@/hooks/useScanData";

import "./sisleyInfoPanel.scss";

const IN_TO_CM = 2.54;

// Ties the toggle button to the region it controls for screen readers.
const BODY_ID = "sisley-info-body";

type Row = { label: string; value: string };

const Chevron = ({ up }: { up: boolean }) => (
  <svg
    className={`sisley-info__chevron${up ? " sisley-info__chevron--up" : ""}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M3 4.5 L6 7.5 L9 4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Formats a garment measure for display.
 *
 * The contract is settled (dashboard `docs/pwa-sisley-d3-handover.md` §2, open
 * question 1, closed 2026-08-11): `garmentMeasures` are raw measurements in
 * **cm**, not graded sizes. Numbers render with at most one decimal and a `cm`
 * suffix; any string value passes through untouched (defensive — the backend
 * only writes numbers). The re-round to 1 dp is deliberate: it normalises a
 * writer that stores more precision. Which service writes the field is still
 * being deduped between two implementations — see `SISLEY_WHITELABEL.md` — but
 * that doesn't reach this function.
 */
const formatMeasure = (value?: string | number): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? `${Number(value.toFixed(1))} cm` : "";
  }
  return value.trim();
};

/**
 * `scans/{id}.height` is stored in INCHES by `ScanModel.create`. Sisley defaults
 * to metric, so cm is the default here too — but a user who flipped the toggle
 * in the New Avatar form has that preference stored, and it wins (same
 * `convertHeight` key `NewAvatarScreen` and `SubmitScan` read).
 */
const formatHeight = (heightInches?: number): string => {
  if (!heightInches || !Number.isFinite(heightInches)) return "";
  const stored = localStorage.getItem("convertHeight");
  const useMetric = stored === null ? true : stored === "true";
  const cm = heightInches * IN_TO_CM;
  return useMetric ? `${Math.round(cm)}cm` : convertCmToFeetInches(cm);
};

const keep = (rows: { label: string; value?: string }[]): Row[] =>
  rows.filter((r): r is Row => !!r.value && r.value.trim().length > 0);

/**
 * Sisley "double-check your info" overlay on the Avatar Preview (Body Scan
 * Procedures doc, Avatar Preview page). Renders ONLY what actually exists:
 *
 * - Details (Gender / Height / Age) come from the scan doc and are always there.
 * - Measured rows come from `garmentMeasures`, written server-side on approval
 *   (live on dev and prod). Absent on any scan approved before 2026-08-11 —
 *   the trigger can't retro-fill — so that half still collapses for those.
 * - Entered rows come from `customerInfo`, which is optional by design; a user
 *   who skipped the tape-measure fields sees none of them.
 *
 * Any empty row, empty half, and the panel itself all disappear rather than
 * rendering a blank or a placeholder. Inert on every non-Sisley tenant.
 */
export const SisleyInfoPanel = ({ scan }: { scan?: ScanDocData | null }) => {
  // Starts OPEN: this review is the point of the screen for a Sisley customer.
  // Deliberately not persisted — every visit opens expanded, so the
  // information can't be permanently hidden by one stray tap.
  const [expanded, setExpanded] = useState(true);

  const { details, measured, entered } = useMemo(() => {
    const customer = scan?.customerInfo ?? {};
    const measures = scan?.garmentMeasures ?? {};

    // The customer picks WHICH waistband they order at (High/Mid/Low) and the
    // backend computes that waistband's measure — so the choice belongs in the
    // label, not in a row of its own. Matches the report CF, which carries all
    // three (circumference ids 30/31/32).
    const waistbandLabel = customer.waistband
      ? `Waistband (${customer.waistband})`
      : "Waistband";

    return {
      details: keep([
        { label: "Gender", value: scan?.gender },
        { label: "Height", value: formatHeight(scan?.height) },
        { label: "Age", value: scan?.age ? String(scan.age) : "" },
      ]),
      measured: keep([
        { label: waistbandLabel, value: formatMeasure(measures.waistband) },
        { label: "Chest", value: formatMeasure(measures.chest) },
        { label: "Sleeve", value: formatMeasure(measures.sleeve) },
        { label: "Inleg", value: formatMeasure(measures.inleg) },
      ]),
      entered: keep([
        { label: "Head", value: customer.headCircumference },
        {
          label: "Dominant hand",
          value: customer.handedness ? `${customer.handedness} handed` : "",
        },
        { label: "Hand", value: customer.handMeasurement },
        { label: "Finger", value: customer.fingerMeasurement },
      ]),
    };
  }, [scan]);

  if (!details.length && !measured.length && !entered.length) return null;

  const renderRows = (rows: Row[]) =>
    rows.map((row) => (
      <div className="sisley-info__row" key={row.label}>
        <dt>{row.label}</dt>
        <dd>{row.value}</dd>
      </div>
    ));

  return (
    <aside
      className={`sisley-info${expanded ? "" : " sisley-info--collapsed"}`}
      aria-label="Your avatar information"
    >
      {/* Collapse control. The panel overlays the avatar, so the customer needs
          a way to get it out of the way and look at the model — but it starts
          open, because the information IS the point of this screen. */}
      <button
        type="button"
        className="sisley-info__toggle"
        aria-expanded={expanded}
        aria-controls={BODY_ID}
        onClick={() => setExpanded((open) => !open)}
      >
        <span className="sisley-info__toggle-label">
          {expanded ? "Hide" : "Your information"}
        </span>
        <Chevron up={expanded} />
      </button>

      <div id={BODY_ID} className="sisley-info__body" hidden={!expanded}>
        {details.length > 0 && (
          <dl className="sisley-info__group">{renderRows(details)}</dl>
        )}

        {(measured.length > 0 || entered.length > 0) && (
          <div className="sisley-info__group">
            <h3 className="sisley-info__heading">Measurements</h3>
            {measured.length > 0 && <dl>{renderRows(measured)}</dl>}
            {entered.length > 0 && (
              <>
                {measured.length > 0 && <div className="sisley-info__divider" />}
                <p className="sisley-info__caption">Entered by you</p>
                <dl>{renderRows(entered)}</dl>
              </>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default SisleyInfoPanel;
